import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { session, termsRequired } from "./api/client.ts";
import { locale, t } from "./i18n/index.ts";
import { ignore } from "./lib/ignore.ts";
import { useLinkTarget } from "./lib/linkTarget.ts";
import { fragment } from "./lib/native.ts";
import { herePath, location, navigate, spacePath } from "./lib/router.ts";
import { boot, bootError, homeUrl, me } from "./lib/state.ts";
import { LabPage } from "./office/LabPage.tsx";
import { LAB_PATH, labEnabled } from "./office/labRoute.ts";
import { AccountPrivacy, AccountSessions } from "./pages/AccountPrivacy.tsx";
import { AllowSender } from "./pages/AllowSender.tsx";
import { Code } from "./pages/auth/code.tsx";
import { type AuthPath, authPath, returnTo } from "./pages/auth/flow.ts";
import { ForgotPassword, ResetPassword } from "./pages/auth/password.tsx";
import { SignIn } from "./pages/auth/signIn.tsx";
import { SignUp } from "./pages/auth/signUp.tsx";
import { AcceptTerms } from "./pages/auth/terms.tsx";
import { DeviceData } from "./pages/DeviceData.tsx";
import { OrgPicker } from "./pages/OrgPicker.tsx";
import { Profile } from "./pages/Profile.tsx";
import { PublicItem, SharedItem } from "./pages/PublicItem.tsx";
import { SharePage } from "./pages/Share.tsx";
import { RenderPage } from "./render/RenderPage.tsx";
import { AccountShell, GuestShell, Shell } from "./Shell.tsx";
import { Failed } from "./ui/Crash.tsx";
import { FileDrop } from "./ui/FileDrop.tsx";
import { EmptyState } from "./ui/misc.tsx";
import { Spinner } from "./ui/Spinner.tsx";
import { Toaster } from "./ui/Toaster.tsx";

function Center({ children }: { children: ComponentChildren }) {
  return <div class="flex min-h-dvh items-center justify-center p-4">{children}</div>;
}

/** The whole window waiting (the session, a link): a spinner in its middle. */
function PageSpinner() {
  return (
    <Center>
      <Spinner />
    </Center>
  );
}

/** The sign-in pages, by path. */
const AUTH: Readonly<Record<AuthPath, () => ComponentChildren>> = {
  "/signin": () => <SignIn />,
  "/signup": () => <SignUp />,
  "/code": () => <Code />,
  "/forgot-password": () => <ForgotPassword />,
  "/reset-password": () => <ResetPassword />,
};

/** Signed in, but the Terms in force are still to accept (never, or they changed): nothing else until they are. */
const termsDue = (): boolean => termsRequired.value || (me.value !== null && !me.value.terms.accepted_at);

/** A public item's path: this, then its link's secret. */
const PUBLIC = "/p/";
/** The render page's path (a picture of an item's document for an agent): this, then its link's token. */
const RENDER = "/render/";
/** A short link's path: this, then an item id. */
const SHORT = "/i/";
/** An item shared with you, its guest: this, then its id. */
const GUEST = "/g/";
const ITEM_ID = /^itm_[a-z0-9]+$/u;
const NOT_FOUND = 404;

/** Your account's pages, by path. */
const ACCOUNT: Readonly<Record<string, () => ComponentChildren>> = {
  "/profile": () => <Profile />,
  "/profile/orgs": () => <OrgPicker />,
  "/profile/sessions": () => <AccountSessions />,
  "/profile/privacy": () => <AccountPrivacy />,
  "/profile/device": () => <DeviceData />,
  "/allow-sender": () => <AllowSender />,
};

/** Goes to `to` instead (replacing this address in the history). */
function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, true), [to]);
  return null;
}

/**
 * A short link (`/i/<item id>`): the item's page, in whichever space it's in, for whoever may open it; for anyone
 * else, as if it didn't exist.
 */
function ShortLink({ id }: { id: string }) {
  const { r } = useLinkTarget(`/links/${id}`);
  // Not there (or not for you) is one thing; not reachable right now (the network, the server) another.
  if (r.error) {
    return (
      <AccountShell>
        {r.error.status === NOT_FOUND ? (
          <EmptyState title={t("link.missing")}>{t("link.missingHint")}</EmptyState>
        ) : (
          <Failed error={r.error} onRetry={r.reload} />
        )}
      </AccountShell>
    );
  }
  return <PageSpinner />;
}

function Routes() {
  // Where what's shared goes, in the app's share extension: that page alone, wherever it opened.
  if (fragment() === "share") {
    return <SharePage />;
  }
  const path = location.value.pathname;
  const short = path.slice(SHORT.length);
  if (path.startsWith(SHORT) && ITEM_ID.test(short)) {
    return <ShortLink id={short} />;
  }
  const shared = path.slice(GUEST.length);
  if (path.startsWith(GUEST) && ITEM_ID.test(shared)) {
    return (
      <AccountShell wide={true}>
        <SharedItem id={shared} />
      </AccountShell>
    );
  }
  // Read on every page, so `?lab=1` anywhere turns the lab on (the page it's on then goes where it would).
  const lab = labEnabled(location.value.search);
  if (path === LAB_PATH && lab) {
    return (
      <AccountShell wide={true}>
        <LabPage />
      </AccountShell>
    );
  }
  const account = ACCOUNT[path];
  if (account) {
    return <AccountShell>{account()}</AccountShell>;
  }
  const space = spacePath(path)?.space;
  // Anywhere else goes home: the last used space (if still a member), else the space picker.
  return space ? <Shell spaceId={space} /> : <Redirect to={homeUrl()} />;
}

/** A public item's page (`/p/<token>`): in your frame when signed in, a guest's when not (or yours can't load). */
function Public({ token }: { token: string }) {
  // Your session couldn't load (the network, the server): the page as anyone sees it.
  const s = bootError.value ? null : session.value;
  if (s === undefined || (s && me.value === null)) {
    return <PageSpinner />;
  }
  if (s && termsDue()) {
    return <AcceptTerms />;
  }
  return s ? (
    <AccountShell wide={true}>
      <PublicItem token={token} />
    </AccountShell>
  ) : (
    <GuestShell>
      <PublicItem token={token} />
    </GuestShell>
  );
}

function Body() {
  const s = session.value;
  const path = location.value.pathname;
  // Public pages open without signing in.
  if (path.startsWith(PUBLIC)) {
    return <Public token={path.slice(PUBLIC.length)} />;
  }
  // As is the render page, by its link's token (render/RenderPage.tsx).
  if (path.startsWith(RENDER)) {
    return <RenderPage token={path.slice(RENDER.length)} />;
  }
  if (bootError.value) {
    return (
      <Center>
        <Failed
          error={bootError.value}
          onRetry={() => {
            boot().catch(ignore);
          }}
        />
      </Center>
    );
  }
  const auth = AUTH[path as AuthPath];
  if (auth && s !== undefined) {
    // Signed in already: on to where signing in would go. A reset link still opens: it's for whoever has it.
    return s && path !== "/reset-password" ? <Redirect to={returnTo()} /> : auth();
  }
  if (s === null) {
    return <Redirect to={authPath("/signin", {}, herePath())} />;
  }
  if (s === undefined || me.value === null) {
    return <PageSpinner />;
  }
  if (termsDue()) {
    return <AcceptTerms />;
  }
  return <Routes />;
}

export function App() {
  // Reading the locale here re-renders the whole tree when the language changes.
  return (
    <div lang={locale.value}>
      <Body />
      <FileDrop />
      <Toaster />
    </div>
  );
}
